import React, { useRef, useEffect, useState, useMemo } from 'react';
import Globe from 'react-globe.gl';
import { useTranslation } from 'react-i18next';
import { aggregateDataByCountry, getMachinesByCountry } from '../utils/globeDataUtils';
import type { CountryData } from '../utils/globeDataUtils';
import { getCountryFlag } from '../utils/countryFlags';
import type { CustomerGroup, Machine } from '../services/BackendDataService';
import LazyMachineGrid from './LazyMachineGrid';
import { translateCountryName } from '../utils/sapMetadataTranslations';
import { styles, cx } from '../styles';
import { useMediaQuery } from '@digitalisation/enlixy-comp';
import { useThemeColorValues, useThemeClasses } from '../providers/ThemeProvider';

interface GlobeViewProps {
  sapData: Record<string, CustomerGroup>;
  onMachineClick?: (machine: Machine) => void;
}

const GlobeView: React.FC<GlobeViewProps> = ({ sapData, onMachineClick }) => {
  const { t } = useTranslation();
  const globeRef = useRef<any>(null);
  const [selectedCountry, setSelectedCountry] = useState<string | null>(null);
  const [hoveredCountry, setHoveredCountry] = useState<CountryData | null>(null);
  const [globeReady, setGlobeReady] = useState(false);
  // Below Tailwind's sm breakpoint (640 px)
  const isMobile = useMediaQuery('(max-width: 639.98px)');

  // Get theme colors
  const themeColorValues = useThemeColorValues();
  const themeClasses = useThemeClasses();

  // Debug logging (only in development)
  if (import.meta.env.DEV) {
    console.log('🌍 GlobeView: Received sapData:', sapData);
    console.log('🌍 GlobeView: Number of customer groups:', Object.keys(sapData).length);
  }

  // Aggregate machine data by country
  const countryData = useMemo(() => {
    const data = aggregateDataByCountry(sapData);
    if (import.meta.env.DEV) {
      console.log('🌍 GlobeView: Aggregated country data:', data);
    }
    return data;
  }, [sapData]);
  
  // Get machines for selected country
  const selectedCountryMachines = useMemo(() => {
    if (!selectedCountry) return [];
    return getMachinesByCountry(selectedCountry, sapData);
  }, [selectedCountry, sapData]);

  // Auto-rotate globe when no interaction
  useEffect(() => {
    if (!globeRef.current || !globeReady) return;
    
    let autoRotateTimer: NodeJS.Timeout | null = null;
    
    const startAutoRotate = () => {
      autoRotateTimer = setInterval(() => {
        if (globeRef.current) {
          const controls = globeRef.current.controls();
          if (controls) {
            controls.autoRotate = true;
            controls.autoRotateSpeed = 0.5;
          }
        }
      }, 100);
    };

    const stopAutoRotate = () => {
      if (autoRotateTimer) {
        clearInterval(autoRotateTimer);
        autoRotateTimer = null;
      }
      if (globeRef.current) {
        const controls = globeRef.current.controls();
        if (controls) {
          controls.autoRotate = false;
        }
      }
    };

    // Start auto rotation
    startAutoRotate();

    // Stop on user interaction
    const handleInteractionStart = () => stopAutoRotate();
    const handleInteractionEnd = () => {
      setTimeout(startAutoRotate, 3000); // Resume after 3 seconds of inactivity
    };

    if (globeRef.current) {
      const globeElement = globeRef.current.scene();
      if (globeElement) {
        globeElement.addEventListener('mousedown', handleInteractionStart);
        globeElement.addEventListener('mouseup', handleInteractionEnd);
        globeElement.addEventListener('touchstart', handleInteractionStart);
        globeElement.addEventListener('touchend', handleInteractionEnd);
      }
    }

    return () => {
      if (autoRotateTimer) {
        clearInterval(autoRotateTimer);
      }
      stopAutoRotate();
    };
  }, [globeReady]);

  // Handle point click
  const handlePointClick = (point: CountryData) => {
    setSelectedCountry(point.country);
    
    // Smooth transition to country
    if (globeRef.current) {
      globeRef.current.pointOfView({
        lat: point.lat,
        lng: point.lng,
        altitude: 1.5
      }, 1500);
    }
  };

  // Handle point hover
  const handlePointHover = (point: CountryData | null) => {
    setHoveredCountry(point);
  };

  // Calculate point size based on machine count
  const getPointSize = (machineCount: number) => {
    const maxCount = Math.max(...countryData.map(d => d.machineCount));
    const minSize = 0.8;  // Increased from 0.3
    const maxSize = 2.5;  // Increased from 1.2
    return minSize + (machineCount / maxCount) * (maxSize - minSize);
  };

  // Generate point color based on machine count
  const getPointColor = (machineCount: number) => {
    const maxCount = Math.max(...countryData.map(d => d.machineCount));
    const intensity = machineCount / maxCount;
    
    // Use theme colors for better dark mode support
    if (intensity < 0.3) return themeColorValues.info; // Blue for few machines
    if (intensity < 0.6) return themeColorValues.warning; // Orange/Yellow for moderate
    return themeColorValues.error; // Red for many machines
  };

  const handleMachineCardClick = (machine: Machine) => {
    if (onMachineClick) {
      onMachineClick(machine);
    }
  };

  return (
    <div className={cx(styles.globe.container, "space-y-4 sm:space-y-6")}>
      {/* Globe Container */}
      <div className={cx(themeClasses.bgSurface, 'rounded-lg shadow-md overflow-hidden border', themeClasses.borderPrimary)}>
        <div className={cx('p-3 sm:p-4 border-b', themeClasses.borderPrimary)}>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-0">
            <div>
              <h3 className={cx('text-base sm:text-lg font-semibold', themeClasses.textPrimary)}>
                {t('globalMachineDistribution') || 'Global Machine Distribution'}
              </h3>
            </div>
            {selectedCountry && (
              <button
                onClick={() => {
                  setSelectedCountry(null);
                  if (globeRef.current) {
                    globeRef.current.pointOfView({ altitude: 2.5 }, 1500);
                  }
                }}
                className={cx('px-3 py-1.5 sm:px-4 sm:py-2 rounded-md transition-colors text-xs sm:text-sm font-medium w-full sm:w-auto', themeClasses.bgTertiary, themeClasses.hover, themeClasses.textPrimary)}
              >
                {t('showAllCountries') || 'Show All Countries'}
              </button>
            )}
          </div>
        </div>
        
        <div className={cx(styles.globe.canvas.container, "h-[300px] sm:h-[500px] min-h-[300px]", "relative overflow-hidden bg-black/5 flex items-center justify-center")}>
          {/* Debug info */}
          {!globeReady && (
            <div className="absolute inset-0 flex items-center justify-center bg-gray-100 text-gray-600 z-10">
              <div className="text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
                <div className="text-sm">Loading Globe...</div>
              </div>
            </div>
          )}
          
          <div className="w-full h-full flex items-center justify-center">
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%', height: '100%' }}>
              <Globe
              ref={globeRef}
              width={undefined}
              height={isMobile ? 300 : 500} // Use mobile-aware height
              backgroundColor="rgba(0,0,0,0.1)" // Add a slight background to make it visible
            globeImageUrl="//unpkg.com/three-globe/example/img/earth-blue-marble.jpg"
            bumpImageUrl="//unpkg.com/three-globe/example/img/earth-topology.png"
            backgroundImageUrl="//unpkg.com/three-globe/example/img/night-sky.png"
            showGlobe={true}
            showGraticules={false}
            showAtmosphere={true}
            atmosphereColor="rgba(100, 149, 237, 0.7)"
            atmosphereAltitude={0.15}
            
            // Main points - larger and more visible
            pointsData={countryData}
            pointLat="lat"
            pointLng="lng"
            pointAltitude={0.03}  // More elevation for better visibility
            pointRadius={(d: any) => getPointSize((d as CountryData).machineCount)}
            pointColor={(d: any) => getPointColor((d as CountryData).machineCount)}
            pointResolution={16}  // Even higher resolution for smoother appearance
            
            pointLabel={(d: any) => {
              const data = d as CountryData;
              return `
              <div style="
                background: rgba(0,0,0,0.9); 
                color: white; 
                padding: 10px 14px; 
                border-radius: 8px; 
                font-size: 13px; 
                max-width: 220px;
                text-align: center;
                box-shadow: 0 4px 12px rgba(0,0,0,0.3);
                border: 1px solid rgba(255,255,255,0.1);
              ">
                <div style="font-weight: bold; margin-bottom: 6px; font-size: 14px;">
                  ${getCountryFlag(data.country)} ${translateCountryName(data.country)}
                </div>
                <div style="color: ${themeColorValues.info}; font-weight: 600;">${data.machineCount} ${data.machineCount === 1 ? 'machine' : 'machines'}</div>
                <div style="margin-top: 4px; font-size: 11px; color: ${themeColorValues.textMuted};">
                  ${data.factories.length} ${data.factories.length === 1 ? 'factory' : 'factories'}
                </div>
              </div>
            `;
            }}
            onPointClick={(point: any) => handlePointClick(point as CountryData)}
            onPointHover={(point: any) => handlePointHover(point as CountryData | null)}
            onGlobeReady={() => {
              console.log('🌍 Globe ready, mobile:', isMobile);
              setGlobeReady(true);
              
              // Configure controls for mobile
              if (globeRef.current) {
                const controls = globeRef.current.controls();
                if (controls && isMobile) {
                  console.log('🌍 Configuring mobile controls');
                  controls.enableDamping = true;
                  controls.dampingFactor = 0.05;
                  controls.enableZoom = true;
                  controls.enableRotate = true;
                  controls.enablePan = false; // Disable panning on mobile for better UX
                }
              }
            }}
            animateIn={true}
          />
            </div>
          </div>
          
          {/* Legend - Hidden on mobile, shown on desktop */}
          <div className={cx(styles.globe.legend.container, "hidden sm:block")}>
            <div className={styles.globe.legend.title}>{t('legend') || 'Legend'}</div>
            <div className={styles.globe.legend.list}>
              <div className={styles.globe.legend.item}>
                <div className={cx(styles.globe.legend.indicator, themeClasses.bgInfo)}></div>
                <span className={styles.globe.legend.text}>{t('fewMachines') || 'Few machines'}</span>
              </div>
              <div className={styles.globe.legend.item}>
                <div className={cx(styles.globe.legend.indicator, themeClasses.bgWarning)}></div>
                <span className={styles.globe.legend.text}>{t('moderateMachines') || 'Moderate machines'}</span>
              </div>
              <div className={styles.globe.legend.item}>
                <div className={cx(styles.globe.legend.indicator, themeClasses.bgError)}></div>
                <span className={styles.globe.legend.text}>{t('manyMachines') || 'Many machines'}</span>
              </div>
              <div className={styles.globe.legend.footer}>
                <span className={styles.globe.legend.footerText}>{t('clickCountries') || 'Click countries to explore machines'}</span>
              </div>
            </div>
          </div>

          {/* Country stats overlay - Responsive positioning */}
          {hoveredCountry && (
            <div className={cx('absolute backdrop-blur-sm p-3 sm:p-4 rounded-lg shadow-lg border max-w-xs', 
              'top-2 right-2 sm:top-4 sm:right-4', // Smaller positioning on mobile
              'text-xs sm:text-sm', // Smaller text on mobile
              themeClasses.bgSurface, themeClasses.borderPrimary)}>
              <div className={cx('font-semibold mb-2', themeClasses.textPrimary)}>
                {getCountryFlag(hoveredCountry.country)} {translateCountryName(hoveredCountry.country)}
              </div>
              <div className={cx('space-y-1', themeClasses.textSecondary)}>
                <div className="space-y-1">
                  <span className="font-medium">{t('machines') || 'Machines'}:</span> {hoveredCountry.machineCount}
                </div>
                <div className="space-y-1">
                  <span className="font-medium">{t('factories') || 'Factories'}:</span> {hoveredCountry.factories.length}
                </div>
                <div className={cx('text-[10px] sm:text-xs mt-2', themeClasses.textMuted)}>
                  {t('clickToExplore') || 'Click to explore machines'}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Mobile Legend - Shown only on mobile below the globe */}
        <div className={cx("block sm:hidden p-3 border-t", themeClasses.borderPrimary)}>
          <div className={cx("text-sm font-medium mb-2", themeClasses.textPrimary)}>{t('legend') || 'Legend'}</div>
          <div className="flex flex-wrap gap-3 text-xs">
            <div className="flex items-center gap-1">
              <div className={cx("w-2 h-2 rounded-full", themeClasses.bgInfo)}></div>
              <span className={themeClasses.textSecondary}>{t('fewMachines') || 'Few machines'}</span>
            </div>
            <div className="flex items-center gap-1">
              <div className={cx("w-2 h-2 rounded-full", themeClasses.bgWarning)}></div>
              <span className={themeClasses.textSecondary}>{t('moderateMachines') || 'Moderate machines'}</span>
            </div>
            <div className="flex items-center gap-1">
              <div className={cx("w-2 h-2 rounded-full", themeClasses.bgError)}></div>
              <span className={themeClasses.textSecondary}>{t('manyMachines') || 'Many machines'}</span>
            </div>
          </div>
          <div className={cx("text-[10px] mt-2", themeClasses.textMuted)}>
            {t('clickCountries') || 'Click countries to explore machines'}
          </div>
        </div>
      </div>

      {/* Selected Country Machines */}
      {selectedCountry && selectedCountryMachines.length > 0 && (
        <div className={cx(themeClasses.bgSurface, 'rounded-lg shadow-md p-3 sm:p-6 border', themeClasses.borderPrimary)}>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-3 sm:mb-4 gap-2 sm:gap-0">
            <h4 className={cx('text-base sm:text-lg font-semibold', themeClasses.textPrimary)}>
              {getCountryFlag(selectedCountry)} {translateCountryName(selectedCountry)} - {selectedCountryMachines.length} {t('machines') || 'Machines'}
            </h4>
            <div className={cx('text-xs sm:text-sm', themeClasses.textSecondary)}>
              {t('clickMachineToView') || 'Click a machine to view details'}
            </div>
          </div>
          
          <LazyMachineGrid
            machines={selectedCountryMachines}
            onMachineClick={handleMachineCardClick}
            initialLoadCount={isMobile ? 4 : 8} // Fewer machines initially on mobile
            loadMoreCount={isMobile ? 4 : 8}
          />
        </div>
      )}

      {/* Stats Summary */}
      <div className={cx(themeClasses.bgSurface, 'rounded-lg shadow-md p-3 sm:p-6 border', themeClasses.borderPrimary)}>
        <h4 className={cx('text-base sm:text-lg font-semibold', themeClasses.textPrimary, 'mb-3 sm:mb-4')}>
          {t('globalSummary') || 'Global Summary'}
        </h4>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          <div className="text-center">
            <div className={cx('text-xl sm:text-2xl font-bold', themeClasses.textPrimary, 'text-blue-600')}>
              {countryData.length}
            </div>
            <div className={cx('text-xs sm:text-sm', themeClasses.textSecondary)}>
              {t('countries') || 'Countries'}
            </div>
          </div>
          <div className="text-center">
            <div className={cx('text-xl sm:text-2xl font-bold', themeClasses.textPrimary, 'text-green-600')}>
              {countryData.reduce((sum, country) => sum + country.machineCount, 0)}
            </div>
            <div className={cx('text-xs sm:text-sm', themeClasses.textSecondary)}>
              {t('totalMachines') || 'Total Machines'}
            </div>
          </div>
          <div className="text-center">
            <div className={cx('text-xl sm:text-2xl font-bold', themeClasses.textPrimary, 'text-orange-600')}>
              {countryData.reduce((sum, country) => sum + country.factories.length, 0)}
            </div>
            <div className={cx('text-xs sm:text-sm', themeClasses.textSecondary)}>
              {t('totalFactories') || 'Total Factories'}
            </div>
          </div>
          <div className="text-center">
            <div className={cx('text-xl sm:text-2xl font-bold', themeClasses.textPrimary, 'text-purple-600')}>
              {Math.round(countryData.reduce((sum, country) => sum + country.machineCount, 0) / countryData.length)}
            </div>
            <div className={cx('text-xs sm:text-sm', themeClasses.textSecondary)}>
              {t('avgMachinesPerCountry') || 'Avg Machines/Country'}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GlobeView;
